import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {FullBotUi, LocationOptionDTO, UpdateBotRequest, WarningUi} from "../../../api/generated";
import {globalFetchConfig, locationApi, RequestStatus} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";
import {FranchiseRequestUi} from "../assistant/franchiseTypes";
import {v4} from "uuid";
import {BACKEND_URL} from "../../../configs";
import {showErrorToastNotification} from "../../elements/toastNotification/toastNotificationSlice";

export type FranchiseRequestPartial = Partial<FranchiseRequestUi>;

export type FranchiseListItem = FranchiseRequestUi & {
    canBeDeleted?: boolean;
    hasWarnings?: boolean;
    warnings?: WarningUi[];
}

export type FranchiseTableRequestUi = {
    searchQuery: string;
    locationPublicIds: string[];
    pageNumber: number;
    pageSize: number;
}

export const FRANCHISE_TABLE_ITEMS_ON_PAGE = 10;

export const defaultFranchiseTableRequest: FranchiseTableRequestUi = {
    searchQuery: "",
    locationPublicIds: [],
    pageNumber: 0,
    pageSize: FRANCHISE_TABLE_ITEMS_ON_PAGE
}

export interface FranchiseState {
    franchises: FranchiseListItem[];
    franchiseOptions: FranchiseListItem[];
    locationOptions: LocationOptionDTO[];
    selectedFranchise?: FranchiseListItem;
    totalCount: number;
    tableRequest: FranchiseTableRequestUi;
    listStatus: RequestStatus;
    detailsStatus: RequestStatus;
}

const initialState: FranchiseState = {
    franchises: [],
    franchiseOptions: [],
    locationOptions: [],
    totalCount: 0,
    tableRequest: defaultFranchiseTableRequest,
    listStatus: RequestStatus.NONE,
    detailsStatus: RequestStatus.NONE,
}

const toLocationOption = (location: any): LocationOptionDTO => ({
    id: location.id,
    publicId: location.publicId,
    locationName: location.name ?? location.locationName,
    vehicleDataProvider: location.vehicleDataProvider,
    locationHash: location.locationHash
});

const toFranchiseListItem = (franchise: any): FranchiseListItem => ({
    id: franchise.id,
    publicId: franchise.publicId,
    name: franchise.name || "",
    franchiseHash: franchise.franchiseHash,
    urlPathPattern: franchise.urlPathPattern,
    focusEnabled: franchise.focusEnabled ?? true,
    sharedWithFocusedFranchises: Boolean(franchise.sharedWithFocusedFranchises),
    franchiseEmailConfigurationId: franchise.franchiseEmailConfigurationId,
    franchiseEmailTranscriptRecipients: Array.isArray(franchise.franchiseEmailTranscriptRecipients)
        ? franchise.franchiseEmailTranscriptRecipients.join(";")
        : franchise.franchiseEmailTranscriptRecipients || "",
    franchiseEmailFiltrationEnabled: Boolean(franchise.franchiseEmailFiltrationEnabled),
    franchiseEmailTranscriptSummaryEnabled: Boolean(franchise.franchiseEmailTranscriptSummaryEnabled),
    publicLocationIdList: (franchise.locationList || []).map(toLocationOption),
    newVehicleDataConfiguration: franchise.newVehicleDataConfiguration,
    knowledgeRecords: franchise.knowledgeRecords || [],
    canBeDeleted: franchise.canBeDeleted,
    hasWarnings: franchise.hasWarnings,
    warnings: franchise.warnings || []
});

const fetchFranchiseTable = async (request: FranchiseTableRequestUi): Promise<{ franchises: FranchiseListItem[]; totalCount: number }> => {
    const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise/table`, {
        method: "POST",
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw new Error("Failed to fetch franchises");
    }
    const data = await response.json();
    return {
        franchises: (data.franchises || []).map(toFranchiseListItem),
        totalCount: data.totalCount || 0
    };
};

const fetchFranchiseOptions = async (): Promise<FranchiseListItem[]> => {
    const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise/options`);
    if (!response.ok) {
        throw new Error("Failed to fetch franchise options");
    }
    const data = await response.json();
    return (data || []).map(toFranchiseListItem);
};

export const fetchFranchises = createAsyncThunk<{
    franchises: FranchiseListItem[];
    franchiseOptions: FranchiseListItem[];
    locationOptions: LocationOptionDTO[];
    totalCount: number;
}, undefined, { state: RootState }>("franchise/fetchList", async (_payload, {getState, dispatch, rejectWithValue}) => {
    const tableRequest = getState().franchise.tableRequest;
    try {
        const locationOptions = await locationApi.getLocationOptions();
        const table = await fetchFranchiseTable(tableRequest);
        const franchiseOptions = await fetchFranchiseOptions();
        return {franchises: table.franchises, franchiseOptions, locationOptions, totalCount: table.totalCount};
    } catch (error: any) {
        // Reported here rather than in each component: six screens dispatch this thunk and several
        // are mounted at once (the assistants list behind its create/edit modal), so a per-component
        // effect would stack up duplicate toasts for a single failure.
        dispatch(showErrorToastNotification({message: "Failed to load franchises and locations"}));
        return rejectWithValue(error?.message ?? "Failed to load franchises and locations");
    }
});

export const fetchFranchiseDetails = createAsyncThunk<{
    franchise?: FranchiseListItem;
}, { franchisePublicId: string }>("franchise/fetchDetails", async ({franchisePublicId}) => {
    const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise/${franchisePublicId}`);
    if (!response.ok) {
        throw new Error("Failed to fetch franchise details");
    }
    const franchise = toFranchiseListItem(await response.json());
    return {franchise};
});

export const mapBotUpdateRequest = (
    bot: FullBotUi,
    franchiseList: FranchiseRequestUi[]
): UpdateBotRequest => ({
    id: bot.id,
    emailConfigurationId: bot.emailConfigurationId,
    publicFranchiseIdList: franchiseList.map(franchise => franchise.publicId),
    urlProcessorConfigurationId: bot.urlProcessorConfigurationId,
    publicId: bot.publicId,
    botType: bot.botType,
    useMultiAgent: (bot as FullBotUi & { useMultiAgent?: boolean }).useMultiAgent ?? false,
    siteName: bot.siteName,
    siteURL: bot.siteURL,
    groupHash: bot.groupHash,
    conversationLimit: bot.conversationLimit,
    workingRange: bot.workingRange,
    teamInfo: bot.teamInfo,
    openAiApiKey: bot.openAiApiKey,
    assistantEmail: bot.assistantEmail || "",
    assistantEmailPassword: bot.assistantEmailPassword || "",
    emailTranscriptRecipients: bot.emailTranscriptRecipients || [],
    lastRenewalDate: bot.lastRenewalDate,
    filtrationEnabled: bot.filtrationEnabled,
    transcriptSummaryEnabled: bot.transcriptSummaryEnabled,
    redirectProvider: bot.redirectProvider,
    urlProcessorEnabled: bot.urlProcessorEnabled,
    matchField: bot.matchField,
    matchType: bot.matchType,
    segmentNumberFromTheEnd: bot.segmentNumberFromTheEnd,
    timezone: bot.timezone,
    defaultLocale: bot.defaultLocale,
    supportedPhoneNumberLocales: bot.supportedPhoneNumberLocales,
} as unknown as UpdateBotRequest);

const buildRequestFranchise = (request: FranchiseRequestPartial): FranchiseRequestUi => ({
    id: request.id,
    publicId: request.publicId || v4(),
    name: request.name || "",
    franchiseHash: request.franchiseHash,
    urlPathPattern: request.urlPathPattern,
    focusEnabled: request.focusEnabled ?? true,
    sharedWithFocusedFranchises: Boolean(request.sharedWithFocusedFranchises),
    franchiseEmailConfigurationId: request.franchiseEmailConfigurationId,
    franchiseEmailTranscriptRecipients: request.franchiseEmailTranscriptRecipients || "",
    franchiseEmailFiltrationEnabled: Boolean(request.franchiseEmailFiltrationEnabled),
    franchiseEmailTranscriptSummaryEnabled: Boolean(request.franchiseEmailTranscriptSummaryEnabled),
    publicLocationIdList: request.publicLocationIdList || [],
    newVehicleDataConfiguration: request.newVehicleDataConfiguration
});

const buildFranchiseBody = (request: FranchiseRequestPartial) => {
    const franchise = buildRequestFranchise(request);
    const {
        knowledgeRecords: _knowledgeRecords,
        publicLocationIdList,
        franchiseEmailTranscriptRecipients,
        ...franchiseRequest
    } = franchise;
    return {
        ...franchiseRequest,
        franchiseEmailTranscriptRecipients: franchiseEmailTranscriptRecipients.split(";").map(email => email.trim()).filter(Boolean),
        publicLocationIdList: publicLocationIdList.map(location => location.publicId)
    };
}

export const createFranchiseRequest = async (request: FranchiseRequestPartial): Promise<void> => {
    const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise`, {
        method: "POST",
        body: JSON.stringify(buildFranchiseBody(request))
    });
    if (!response.ok) {
        throw new Error("Failed to create franchise");
    }
}

export const updateFranchiseRequest = async (request: FranchiseRequestPartial): Promise<void> => {
    const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise`, {
        method: "PUT",
        body: JSON.stringify(buildFranchiseBody(request))
    });
    if (!response.ok) {
        throw new Error("Failed to update franchise");
    }
}

export const deleteFranchise = createAsyncThunk<string, string>(
    "franchise/delete",
    async (publicId) => {
        const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise/${publicId}`, {
            method: "DELETE"
        });
        if (!response.ok) {
            throw new Error("Failed to delete franchise");
        }
        return publicId;
    }
);

export const franchiseSlice = createSlice({
    name: "franchise",
    initialState,
    reducers: {
        updateFranchiseTableRequest: (state, {payload}: PayloadAction<Partial<FranchiseTableRequestUi>>) => {
            state.tableRequest = {
                ...state.tableRequest,
                ...payload,
                pageNumber: payload.pageNumber ?? 0
            };
        },
        resetFranchiseTableRequest: (state) => {
            state.tableRequest = {...defaultFranchiseTableRequest};
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchFranchises.pending, state => {
            state.listStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchFranchises.fulfilled, (state, {payload}) => {
            state.franchises = payload.franchises;
            state.franchiseOptions = payload.franchiseOptions;
            state.locationOptions = payload.locationOptions;
            state.totalCount = payload.totalCount;
            state.listStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchFranchises.rejected, state => {
            state.listStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchFranchiseDetails.pending, state => {
            state.detailsStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchFranchiseDetails.fulfilled, (state, {payload}) => {
            state.selectedFranchise = payload.franchise;
            state.detailsStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchFranchiseDetails.rejected, state => {
            state.detailsStatus = RequestStatus.REJECTED;
        });
        builder.addCase(deleteFranchise.fulfilled, (state, {payload}) => {
            state.franchises = state.franchises.filter(franchise => franchise.publicId !== payload);
            state.franchiseOptions = state.franchiseOptions.filter(franchise => franchise.publicId !== payload);
            state.totalCount = Math.max(0, state.totalCount - 1);
        });
    }
});

export const {
    updateFranchiseTableRequest,
    resetFranchiseTableRequest
} = franchiseSlice.actions;

export const franchiseListSelector = (state: RootState): FranchiseListItem[] => state.franchise.franchises;
export const franchiseOptionsSelector = (state: RootState): FranchiseListItem[] => state.franchise.franchiseOptions;
export const franchiseListStatusSelector = (state: RootState): RequestStatus => state.franchise.listStatus;
export const franchiseDetailsStatusSelector = (state: RootState): RequestStatus => state.franchise.detailsStatus;
export const selectedFranchiseSelector = (state: RootState): FranchiseListItem | undefined => state.franchise.selectedFranchise;
export const franchiseLocationOptionsSelector = (state: RootState): LocationOptionDTO[] => state.franchise.locationOptions;
export const franchiseTableRequestSelector = (state: RootState): FranchiseTableRequestUi => state.franchise.tableRequest;
export const franchiseTotalCountSelector = (state: RootState): number => state.franchise.totalCount;
